import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import axios from 'axios';
import './FlyerAnalysis.css';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

const DETAIL_TABS = ['Products', 'Layout', 'Seasonal', 'Brand Ads', 'Competitive'];

export default function FlyerAnalysisDetail() {
  const { getAccessTokenSilently } = useAuth0();
  const { retailer, date } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const country = searchParams.get('country') || localStorage.getItem('fa_country') || '';

  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [activeTab, setActiveTab] = useState('Products');
  const [loading, setLoading] = useState(true);
  const [brandPopover, setBrandPopover] = useState(null);

  const hasDate = date && date !== '_nodate';

  useEffect(() => {
    if (!country || !retailer || !date) return;
    (async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({
          country,
          retailer_slug: retailer,
          limit: '50',
        });
        if (hasDate) {
          params.set('flyer_date_from', date);
          params.set('flyer_date_to', date);
        } else {
          params.set('no_date', 'true');
        }
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/pages?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedPages = res.data.pages || [];
        setPages(fetchedPages);
        if (fetchedPages.length > 0) setActivePage(fetchedPages[0]);
      } catch (err) {
        console.error('Error fetching pages:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getAccessTokenSilently, country, retailer, date]);

  // Fetch full page detail when activePage changes
  const [pageDetail, setPageDetail] = useState(null);
  useEffect(() => {
    if (!activePage?._id) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/pages/${activePage._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPageDetail(res.data);
      } catch (err) {
        console.error('Error fetching page detail:', err);
      }
    })();
  }, [getAccessTokenSilently, activePage]);

  const products = pageDetail?.products || [];
  const layout = pageDetail?.layout_analysis || {};
  const seasonal = pageDetail?.seasonal_analysis || {};
  const brandAds = pageDetail?.brand_advertising || {};
  const competitive = pageDetail?.competitive_intelligence || {};

  const formatPrice = (val) => {
    if (val == null) return '—';
    return typeof val === 'number' ? `$${val.toFixed(2)}` : val;
  };

  if (loading) {
    return (
      <div className="main-column" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="fa-header">
          <div className="fa-header-left">
            <button className="fa-btn outline-gray" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="fa-header-title">Loading...</h1>
          </div>
        </div>
        <div className="fa-loading">Loading flyer pages...</div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="main-column" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="fa-header">
          <div className="fa-header-left">
            <button className="fa-btn outline-gray" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="fa-header-title">No Pages Found</h1>
          </div>
        </div>
        <div className="fa-empty">
          <p>No analyzed pages for this flyer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-column" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-left">
          <button className="fa-btn outline-gray" onClick={() => navigate(-1)}>← Back</button>
          <div>
            <h1 className="fa-header-title">{activePage?.retailer_name || retailer}</h1>
            <p className="fa-header-subtitle">{hasDate ? date : 'No date'} — {pages.length} pages</p>
          </div>
        </div>
      </div>

      {/* Page Navigator */}
      <div className="fa-page-nav">
        {pages.map((page, i) => (
          <div
            key={page._id || i}
            className={`fa-page-nav-item ${activePage?._id === page._id ? 'active' : ''}`}
            onClick={() => setActivePage(page)}
          >
            {page.image_url ? (
              <img src={page.image_url} alt={`Page ${page.page_number || i + 1}`} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#9ca3af' }}>
                {page.page_number || i + 1}
              </div>
            )}
            <div className="fa-page-nav-label">P{page.page_number || i + 1}</div>
          </div>
        ))}
      </div>

      {/* Side-by-side Layout */}
      <div className="fa-detail-layout">
        {/* Left: Image */}
        <div className="fa-detail-image-panel">
          {activePage?.image_url ? (
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              wheel={{ step: 0.1 }}
            >
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={activePage.image_url} alt="Flyer page" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </TransformComponent>
            </TransformWrapper>
          ) : (
            <div style={{ color: '#6b7280' }}>No image available</div>
          )}
        </div>

        {/* Right: Data Panel */}
        <div className="fa-detail-data-panel">
          <div className="fa-detail-tabs">
            {DETAIL_TABS.map(tab => (
              <button
                key={tab}
                className={`fa-detail-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="fa-detail-content">
            {/* Products Tab */}
            {activeTab === 'Products' && (
              products.length === 0 ? (
                <div className="fa-empty"><p>No products extracted</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="fa-products-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Brand</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Sale</th>
                        <th>Discount</th>
                        <th>Size</th>
                        <th>Position</th>
                        <th>Brand Ad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{p.product_name || '—'}</td>
                          <td style={{ color: '#6b7280' }}>{p.brand || '—'}</td>
                          <td style={{ fontSize: 12, color: '#6b7280' }}>
                            {p.category}{p.subcategory ? ` > ${p.subcategory}` : ''}
                          </td>
                          <td>{formatPrice(p.regular_price)}</td>
                          <td style={{ color: '#16a34a', fontWeight: 500 }}>{formatPrice(p.sale_price)}</td>
                          <td style={{ color: '#ff8264' }}>{p.discount_description || '—'}</td>
                          <td style={{ fontSize: 12 }}>{p.size_in_flyer || '—'}</td>
                          <td style={{ fontSize: 12, color: '#6b7280' }}>{p.position ? (typeof p.position === 'object' ? p.position.quadrant || '—' : p.position) : '—'}</td>
                          <td>
                            {p.is_brand_ad ? (
                              <span
                                className="fa-brand-badge"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBrandPopover(brandPopover === i ? null : i);
                                }}
                              >
                                Brand Ad
                                {brandPopover === i && p.brand_ad_details && (
                                  <div className="fa-brand-popover" onClick={(e) => e.stopPropagation()}>
                                    {Object.entries(p.brand_ad_details).map(([k, v]) => (
                                      <div key={k} style={{ marginBottom: 4 }}>
                                        <span style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>{k.replace(/_/g, ' ')}: </span>
                                        <span style={{ fontWeight: 500 }}>{String(v)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Layout Tab */}
            {activeTab === 'Layout' && (
              <div className="fa-kv-grid">
                <KVItem label="Page Role" value={layout.page_role || '—'} />
                <KVItem label="Layout Type" value={layout.layout_type || '—'} />
                <KVItem label="Grid" value={layout.grid ? `${layout.grid.rows}x${layout.grid.columns}` : '—'} />
                <KVItem label="Heroes" value={layout.hero_count ?? '—'} />
                <KVItem label="Density" value={layout.density || '—'} />
                <KVItem label="Visual Style" value={layout.visual_style || '—'} />
                <KVItem label="Store Branding" value={layout.store_branding ? 'Yes' : 'No'} />
                <KVItem label="Coupon Section" value={layout.coupon_section ? 'Yes' : 'No'} />
              </div>
            )}

            {/* Seasonal Tab */}
            {activeTab === 'Seasonal' && (
              <div className="fa-kv-grid">
                <KVItem label="Season" value={seasonal.season || '—'} />
                <KVItem label="Holidays" value={seasonal.holidays?.join(', ') || 'None'} />
                <KVItem label="Seasonal Indicators" value={seasonal.seasonal_indicators?.join(', ') || 'None'} />
                <KVItem label="Cultural Focus" value={seasonal.cultural_focus || '—'} />
              </div>
            )}

            {/* Brand Ads Tab */}
            {activeTab === 'Brand Ads' && (
              <div className="fa-kv-grid">
                <KVItem label="Has Brand Ads" value={brandAds.has_brand_ads ? 'Yes' : 'No'} />
                <KVItem label="Ad Count" value={brandAds.ad_count ?? '—'} />
                <KVItem label="Page Coverage" value={brandAds.page_coverage != null ? `${brandAds.page_coverage}%` : '—'} />
                <KVItem label="Sponsoring Brands" value={brandAds.sponsoring_brands?.join(', ') || '—'} />
                <KVItem label="Ad Types" value={brandAds.ad_types?.join(', ') || '—'} />
              </div>
            )}

            {/* Competitive Tab */}
            {activeTab === 'Competitive' && (
              <div className="fa-kv-grid">
                <KVItem label="Price Match Claims" value={competitive.price_match_claims ? 'Yes' : 'No'} />
                <KVItem label="Competitor Callouts" value={competitive.competitor_callouts?.join(', ') || 'None'} />
                <KVItem label="Loyalty Mentions" value={competitive.loyalty_mentions ? 'Yes' : 'No'} />
                <KVItem label="Delivery Promos" value={competitive.delivery_promos ? 'Yes' : 'No'} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KVItem({ label, value }) {
  return (
    <div className="fa-kv-item">
      <div className="fa-kv-label">{label}</div>
      <div className="fa-kv-value">{value}</div>
    </div>
  );
}
